import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { CartRowsUpdaterService } from './services/cart-rows-updater.service';
import { ResumeUpdaterService } from './services/cart-resume-updater.service';
import { DeliveryStepsService } from '../payment/services/delivery-steps.service';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import {
    ActivatedRoute,
    NavigationEnd,
    NavigationStart,
    Router
} from '@angular/router';
import { CommonHttpService } from '../shared/services/http.service';
import { Response } from '@angular/http';
import { PlanModel } from '../shared/models/plan.model';
import { ClientModel } from '../shared/models/client.model';
import { OauthService } from '../shared/services/oauth.service';
import { SubscriptionModel } from '../shared/models/subscription.model';
import { CartItemType } from '../shared/enums/CartItemType';
import { FacebookService } from '../shared/services/facebook.service';
import { DataLayerService } from '../shared/data-layer/data-layer.service';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { Subscription } from 'rxjs/Subscription';
import { of } from 'rxjs/observable/of';
import { switchMap } from 'rxjs/operators';

@AutoUnsubscribe()
@Component({
    selector: 'cart',
    templateUrl: './cart.component.html',
    styleUrls: ['./cart.component.scss']
})
export class CartComponent implements OnInit, AfterViewInit, OnDestroy {
    public client: ClientModel;
    public subscriptions: Array<SubscriptionModel> = [];
    public items = [];
    public loading = true;
    public recurrent = false;
    public recurrentAs: PlanModel;
    public extraFields = [];

    public hasGoogleConversionId = false;
    public googleConversionId = null;

    oauthInitiated: Subscription;
    oauthLoadedAuthenticatedUser: Subscription;
    cartRowsUpdatedServiceIndexed: Subscription;
    clientSubscriptionsFetched: Subscription;
    cartPropertiesFetched: Subscription;
    routerEventsSubscribe: Subscription;

    constructor(
        protected router: Router,
        protected activatedRoute: ActivatedRoute,
        protected cartRowsUpdaterService: CartRowsUpdaterService,
        protected resumeUpdaterService: ResumeUpdaterService,
        protected deliveryStepsService: DeliveryStepsService,
        protected titleService: Title,
        protected translate: TranslateService,
        protected http: CommonHttpService,
        protected oauth: OauthService,
        protected facebookService: FacebookService,
        protected dataLayer: DataLayerService
    ) {
        this.routerEventsSubscribe = this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.oauthInitiated = this.oauth.init();
            }
        });
    }

    ngOnInit() {
        this.oauthLoadedAuthenticatedUser = this.oauth.loadedAuthenticatedUser$.subscribe((client: ClientModel) => {
            this.client = client;
            this.clientSubscriptionsFetched = this.fetchSubscriptions();
        });
        this.cartRowsUpdatedServiceIndexed = this.cartRowsUpdaterService.indexed$.subscribe(items => this.items = items);

        this.handleStoreConfigurations(this.activatedRoute.snapshot.data);
        this.handleRestoredItems(this.activatedRoute.snapshot.data);
        this.items.forEach(item => {
            this.facebookService.trackAddToCart(
                `${parseInt(item.quantity, 10) * parseFloat(item.price)}`,
                [item.id],
                this.facebookService.CONTENT_TYPE_PRODUCT,
                item.name,
                [{'id': item.id, 'quantity': item.quantity}]
            );
        });

        this.translate.get('CART.TITLE').subscribe((title: string) => {
            this.titleService.setTitle(title + ' | ' + this.activatedRoute.snapshot.data.storeConfigurations.store_name);
        });

        this.reset();
    }
    ngAfterViewInit() {
        this.cartPropertiesFetched = this.fetchProperties();
    }
    ngOnDestroy() {
        // Overrided by AutoUnsubscribe
    }

    public handleStoreConfigurations(data) {
        const definedStoreConfigurations = data['storeConfigurations'] !== undefined;
        if (!definedStoreConfigurations) {
            return;
        }

        const storeConfigurations = data['storeConfigurations'];
        this.facebookService.configure(storeConfigurations);
        this.facebookService.trackPageView();

        const definedGoogleConversionId = storeConfigurations['google-conversion-id'] !== undefined
            && storeConfigurations['google-conversion-id'] !== null;
        if (!definedGoogleConversionId) {
            return;
        }

        this.hasGoogleConversionId = true;
        this.googleConversionId = storeConfigurations['google-conversion-id'];
    }
    public handleRestoredItems(data) {
        this.items = data.cartRestore || [];
    }

    /**
     * This is called at cart.row.component's updated event and update cart
     * resume data.
     **/
    public onCartRowUpdated(cartItem) {
        this.cartRowsUpdaterService.refresh().subscribe();
        this.resumeUpdaterService.refresh().subscribe();
    }
    /**
     * This is called at cart.row.component's removed event and update cart
     * resume data and items.
     **/
    public onCartRowRemoved(cartItem) {
        this.cartRowsUpdaterService.refresh().subscribe();
        this.resumeUpdaterService.refresh().subscribe();
        this.dataLayer.removed(cartItem);
    }
    /**
     * This is called after cart.recurrent component updated event has
     * triggered.
     **/
    public onCartRecurrentUpdated() {
        this.fetchProperties();
        this.cartRowsUpdaterService.refresh().subscribe();
        this.resumeUpdaterService.refresh().subscribe();
    }
    /**
     * This checks if cart has recurrent_as property.
     **/
    public isRecurrent() {
        return this.recurrent;
    }
    /**
     * Fetch cart properties.
     **/
    public fetchProperties() {
        this.loading = true;
        return this.http
            .get('/checkout/cart/properties')
            .subscribe((response: Response) => {
                const data = response.json();
                this.loading = false;
                this.extraFields = data.extra_fields;
                this.recurrent = data.properties !== undefined
                    && data.properties.recurrent_as !== undefined
                    && data.properties.recurrent_as !== null;

                if (this.isRecurrent()) {
                    this.recurrentAs = data.properties.recurrent_as;
                }
            });
    }
    /**
     * Fetch active subscriptions.
     **/
    public fetchSubscriptions() {
        return this.http
            .useAuth()
            .get(
                `account/clients/${this.client.id}/subscriptions`
                + `?_sort=-id`
                + `&_fields=id,plan->description`
                + `&_with=plan`
                + `&status=active`
            )
            .subscribe((response: Response) => {
                this.subscriptions = response.json().data;
            }, () => {
                this.subscriptions = [];
            });
    }
    /**
     * Subscriptions update component can be called when has some
     * subscription to update and has not plan in cart items.
     *
     * @return boolean
     **/
    public canUpdateSubscriptions() {
        return !this.loading
            && this.subscriptions.length > 0
            && this.items.filter(item => item.type === CartItemType.plan).length === 0;
    }

    /**
     * Reset selected freight, choose payment method and coupon code.
     **/
    private reset() {
        of({}).pipe(
            switchMap(() => {
                this.loading = true;
                return of({});
            }),
            switchMap(() => {
                return this.resumeUpdaterService.clearPaymentMethodDiscount();
            }),
            switchMap(() => {
                return this.resumeUpdaterService.removeCouponCode();
            }),
            switchMap(() => {
                this.deliveryStepsService.resetSelectedFreight();
                this.loading = false;
                return of({});
            })
        ).subscribe();
    }
}



// WEBPACK FOOTER //
// /codebuild/output/src727013012/src/src/app/cart/cart.component.ts